import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X, RotateCcw, ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX, Upload } from 'lucide-react';

const ROOM_STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700;800&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

:root {
  --ir-wall-0: #ffffff;
  --ir-wall-1: #ffffff;
  --ir-metal-0: #ffffff;
  --ir-metal-1: #000000;

  --ir-cyan: #0c6d75;
  --ir-cyan-dim: #74b9be;
  --ir-blue: #2f5fd1;
  --ir-amber: #a3690a;
  --ir-red: #b83226;

  --ir-paper: #ffffff;
  --ir-ink: #000000;

  --ir-text-hi: #000000;
  --ir-text-mid: #454545;
  --ir-text-low: #595959;

  --ir-font-display: 'Barlow Condensed', 'Arial Narrow', sans-serif;
  --ir-font-body: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  --ir-font-data: 'IBM Plex Mono', 'Roboto Mono', monospace;

  --ir-ease-out: cubic-bezier(0.16, 1, 0.3, 1);
}

/* ==========================================================================
   THE ROOM — one focused beat at a time, connected by a flow strip
   ========================================================================== */

.ir-viewport { container-type: inline-size; container-name: ir; width: 100%; }

.ir-stage {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 10px;
  background: var(--ir-wall-0);
  font-family: var(--ir-font-body);
  color: var(--ir-text-hi);
  box-shadow: 0 30px 70px rgba(20,24,27,.18);
  border: 1px solid rgba(20,24,27,.06);
  opacity: 0;
  transform: scale(1.01);
  transition: opacity .45s var(--ir-ease-out), transform .5s var(--ir-ease-out);
}
.ir-stage.is-mounted { opacity: 1; transform: scale(1); }
.ir-stage *, .ir-stage *::before, .ir-stage *::after { box-sizing: border-box; }

.ir-walls {
  position: absolute; inset: 0;
  background:
    radial-gradient(120% 90% at 50% 0%, rgba(0,0,0,.03), transparent 55%),
    radial-gradient(70% 55% at 85% 100%, rgba(0,0,0,.02), transparent 60%),
    linear-gradient(180deg, var(--ir-wall-1) 0%, var(--ir-wall-0) 75%);
}
.ir-vignette {
  position: absolute; inset: 0; pointer-events: none; z-index: 20;
  background: radial-gradient(120% 90% at 50% 45%, transparent 55%, rgba(20,24,27,.08) 100%);
}

.ir-frame {
  position: relative; z-index: 2; height: 100%;
  display: flex; flex-direction: column;
  padding: 3.2cqw 3.4cqw 3cqw;
  gap: 1.6cqw;
}

/* ---- Header: minimal, just orientation ---- */
.ir-header {
  flex: 0 0 auto;
  display: flex; align-items: baseline; justify-content: space-between;
}
.ir-kicker {
  font-family: var(--ir-font-data);
  font-size: clamp(8px, .95cqw, 13px);
  letter-spacing: .28em; text-transform: uppercase;
  color: var(--ir-cyan); opacity: .85;
}
.ir-header-right { display: flex; align-items: center; gap: .8cqw; }
.ir-category {
  font-family: var(--ir-font-data);
  font-size: clamp(8px, .78cqw, 11px);
  letter-spacing: .14em; color: var(--ir-blue);
  border: 1px solid rgba(47,95,209,.4); background: rgba(47,95,209,.07);
  padding: .3em .7em; border-radius: 999px;
}
.ir-case-tag {
  font-family: var(--ir-font-data);
  font-size: clamp(9px, .95cqw, 13px);
  letter-spacing: .05em; color: var(--ir-ink);
  background: var(--ir-paper); padding: .4em .8em; border-radius: 2px;
  border: 1.5px solid #000000;
  transform: rotate(-1.2deg); box-shadow: 0 5px 12px rgba(20,24,27,.16);
}

/* ---- Flow strip: the "flowchart" ---- */
.ir-flow {
  flex: 0 0 auto;
  display: flex; align-items: center;
}
.ir-node-btn {
  appearance: none; border: none; background: transparent; padding: 0; cursor: pointer;
  display: flex; flex-direction: column; align-items: center; gap: .45cqw;
  font-family: var(--ir-font-data);
  color: var(--ir-text-low);
}
.ir-node-dot {
  width: 1.5cqw; height: 1.5cqw; min-width: 12px; min-height: 12px;
  border-radius: 50%;
  border: 2px solid var(--ir-text-low);
  background: var(--ir-wall-0);
  transition: border-color .25s ease, background .25s ease, box-shadow .25s ease;
}
.ir-node-label {
  font-size: clamp(7px, .68cqw, 10px);
  letter-spacing: .14em; text-transform: uppercase;
  white-space: nowrap;
  transition: color .25s ease;
}
.ir-node-btn.is-done .ir-node-dot { border-color: var(--ir-cyan-dim); background: var(--ir-cyan-dim); }
.ir-node-btn.is-done .ir-node-label { color: var(--ir-text-mid); }
.ir-node-btn.is-current .ir-node-dot {
  border-color: var(--ir-cyan); background: var(--ir-cyan);
  box-shadow: 0 0 0 4px rgba(12,109,117,.18);
}
.ir-node-btn.is-current .ir-node-label { color: var(--ir-cyan); }
.ir-connector { flex: 1 1 auto; height: 1px; background: var(--ir-text-low); opacity: .3; margin: 0 .8cqw; position: relative; top: -0.9cqw; }
.ir-connector.is-done { background: var(--ir-cyan-dim); opacity: .8; }

/* ---- Focus stage: exactly one thing on screen ---- */
.ir-focus {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid rgba(20,24,27,.08);
  box-shadow: 0 20px 50px rgba(20,24,27,.1), inset 0 0 50px rgba(20,24,27,.04);
  overflow: hidden;
}
.ir-focus::before {
  content: ''; position: absolute; inset: 0; z-index: 1; pointer-events: none; mix-blend-mode: multiply;
  background: repeating-linear-gradient(180deg, rgba(20,24,27,.015) 0px, transparent 1px, transparent 3px);
}
.ir-focus-inner {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 4cqw 6cqw;
  text-align: center;
  animation: ir-focus-in .55s var(--ir-ease-out) both;
}
@keyframes ir-focus-in {
  0% { opacity: 0; transform: translateY(14px); }
  100% { opacity: 1; transform: translateY(0); }
}
.ir-focus-eyebrow {
  font-family: var(--ir-font-data);
  font-size: clamp(9px, .95cqw, 14px);
  letter-spacing: .3em; text-transform: uppercase;
  color: var(--ir-cyan); margin-bottom: 1.4cqw;
}

/* Subject */
.ir-focus-title {
  font-family: var(--ir-font-display); font-weight: 700; text-transform: uppercase;
  font-size: clamp(24px, 5.4cqw, 68px); line-height: 1.02; color: var(--ir-text-hi);
  max-width: 90%;
}

/* Evidence */
.ir-evidence-frame {
  position: relative;
  width: 100%; height: 100%;
  border-radius: 4px; overflow: hidden;
  background: #ffffff; border: 1px solid rgba(20,24,27,.08);
}
.ir-evidence-img { width: 100%; height: 100%; object-fit: cover; display: block; filter: grayscale(.15) contrast(1.05); }
.ir-evidence-placeholder {
  width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
  font-family: var(--ir-font-data); color: var(--ir-text-low); letter-spacing: .16em;
  font-size: clamp(10px, 1.1cqw, 15px); padding: 0 8%;
}
.ir-scanline {
  position: absolute; left: 0; top: 0; width: 100%; height: 2px;
  background: linear-gradient(90deg, transparent, var(--ir-cyan), transparent);
  box-shadow: 0 0 12px 2px rgba(12,109,117,.55);
  animation: ir-scan 3.2s linear .3s infinite;
}
@keyframes ir-scan { 0% { top: 0%; opacity: 0; } 4% { opacity: 1; } 96% { opacity: 1; } 100% { top: 100%; opacity: 0; } }
.ir-evidence-corner { position: absolute; width: 16px; height: 16px; border: 2px solid var(--ir-cyan); opacity: .85; }
.ir-evidence-corner.tl { top: 8px; left: 8px; border-right: none; border-bottom: none; }
.ir-evidence-corner.tr { top: 8px; right: 8px; border-left: none; border-bottom: none; }
.ir-evidence-corner.bl { bottom: 8px; left: 8px; border-right: none; border-top: none; }
.ir-evidence-corner.br { bottom: 8px; right: 8px; border-left: none; border-top: none; }

/* Motive */
.ir-focus-text {
  font-family: var(--ir-font-body); font-weight: 500;
  font-size: clamp(15px, 2.1cqw, 27px); line-height: 1.4; color: var(--ir-text-hi);
  max-width: 78%;
}

/* Streaming cursor, used on Subject + Motive while text is typing in */
.ir-type-cursor {
  display: inline-block;
  width: 2px;
  height: 0.85em;
  margin-left: .08em;
  background: var(--ir-cyan);
  vertical-align: -0.08em;
  animation: ir-cursor-blink 1s steps(1) infinite;
}
@keyframes ir-cursor-blink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }

/* Alternatives */
.ir-alt-grid {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 1cqw;
  width: 100%; max-width: 46cqw;
}
.ir-alt-grid li {
  font-family: var(--ir-font-data);
  font-size: clamp(11px, 1.35cqw, 18px); letter-spacing: .03em;
  color: var(--ir-text-mid);
  padding: .8em 1em;
  border: 1px solid rgba(20,24,27,.1);
  border-left: 3px solid rgba(20,24,27,.16);
  background: rgba(20,24,27,.02);
  border-radius: 0 4px 4px 0;
  text-align: left;
  opacity: 0; transform: translateX(-8px);
  animation: ir-alt-in .4s var(--ir-ease-out) forwards;
}
@keyframes ir-alt-in { to { opacity: 1; transform: translateX(0); } }
.ir-alt-grid li.is-flagged {
  color: var(--ir-red); border-left-color: var(--ir-red); border-color: rgba(184,50,38,.35);
  background: rgba(184,50,38,.08);
}

/* WTF */
.ir-meter-big { width: 100%; max-width: 40cqw; }
.ir-meter-track {
  width: 100%; height: 1.4cqw; min-height: 10px; border-radius: 999px; overflow: hidden;
  background: rgba(20,24,27,.05); border: 1px solid rgba(20,24,27,.1);
}
.ir-meter-fill {
  height: 100%; border-radius: 999px;
  background: linear-gradient(90deg, var(--ir-cyan-dim), var(--ir-amber) 65%, var(--ir-red) 100%);
  box-shadow: 0 0 14px rgba(163,105,10,.3);
  transition: width 1.2s cubic-bezier(.65,0,.35,1);
  width: 0%;
}
.ir-meter-ticks { display: flex; justify-content: space-between; margin-top: .5cqw; opacity: .5; }
.ir-meter-ticks span { width: 1px; height: 6px; background: var(--ir-text-low); }
.ir-meter-value {
  font-family: var(--ir-font-data); font-weight: 600; color: var(--ir-amber);
  font-size: clamp(30px, 5.2cqw, 64px); margin-top: 1cqw; transition: color .4s ease;
}
.ir-meter-max { font-family: var(--ir-font-data); color: var(--ir-text-low); font-size: clamp(11px, 1.1cqw, 15px); }

/* Verdict */
.ir-stamp {
  font-family: var(--ir-font-display); font-weight: 800; text-transform: uppercase;
  font-size: clamp(22px, 4.6cqw, 56px); letter-spacing: .04em;
  color: var(--ir-red); border: 4px solid var(--ir-red); border-radius: 6px;
  padding: .2em .7em; max-width: 90%;
  transform: rotate(-4deg) scale(.4); opacity: 0; 
  animation: ir-stamp-impact .55s cubic-bezier(.2,1.4,.4,1) .15s forwards;
}
@keyframes ir-stamp-impact {
  0% { transform: rotate(-4deg) scale(2.1); opacity: 0; filter: blur(6px); }
  55% { transform: rotate(-4deg) scale(.92); opacity: 1; filter: blur(0); }
  75% { transform: rotate(-4deg) scale(1.05); }
  100% { transform: rotate(-4deg) scale(1); opacity: 1; box-shadow: 0 0 26px rgba(184,50,38,.2); }
}
.ir-stamp.verdict-accepted { color: var(--ir-cyan); border-color: var(--ir-cyan); }
.ir-stamp.verdict-questionable { color: var(--ir-amber); border-color: var(--ir-amber); }

@media (prefers-reduced-motion: reduce) {
  .ir-stage, .ir-stage * { transition-duration: .01ms !important; animation-duration: .01ms !important; animation-iteration-count: 1 !important; }
}

/* ==========================================================================
   STUDIO SHELL
   ========================================================================== */

.studio { min-height: 100vh; background: #ffffff; color: #1c2024; font-family: var(--ir-font-body); display: flex; flex-direction: column; }
.studio-topbar {
  flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between;
  padding: 14px 20px; border-bottom: 1px solid #000000; background: #ffffff;
}
.studio-brand { display: flex; align-items: center; gap: 10px; }
.studio-brand-dot { width: 9px; height: 9px; border-radius: 50%; background: var(--ir-cyan); box-shadow: 0 0 8px 1px rgba(12,109,117,.5); }
.studio-brand-name { font-family: var(--ir-font-display); font-weight: 700; letter-spacing: .04em; font-size: 18px; text-transform: uppercase; }
.studio-brand-sub { font-family: var(--ir-font-data); font-size: 11px; color: #595959; letter-spacing: .08em; }
.studio-topbar-actions { display: flex; align-items: center; gap: 8px; }

.studio-main { flex: 1 1 auto; display: flex; min-height: 0; }
.studio-stage-wrap {
  flex: 1 1 auto; display: flex; align-items: center; justify-content: center; padding: 32px;
  background: #ffffff;
}
.studio-stage-inner { width: 100%; max-width: 980px; display: flex; flex-direction: column; gap: 16px; }

.studio-transport {
  display: flex; align-items: center; justify-content: center; gap: 12px;
}
.studio-transport-count {
  font-family: var(--ir-font-data); font-size: 12px; color: #595959; letter-spacing: .08em; min-width: 90px; text-align: center;
}

.studio-sidebar {
  flex: 0 0 380px; max-width: 380px; overflow-y: auto;
  background: #ffffff; border-left: 1px solid #000000; padding: 20px 20px 40px;
  display: flex; flex-direction: column; gap: 22px;
}
.studio-section { display: flex; flex-direction: column; gap: 10px; }
.studio-section-title {
  font-family: var(--ir-font-data); font-size: 11px; letter-spacing: .2em; text-transform: uppercase;
  color: var(--ir-cyan); display: flex; align-items: center; justify-content: space-between;
}
.studio-field { display: flex; flex-direction: column; gap: 6px; }
.studio-label { font-size: 12px; color: #595959; font-weight: 500; }
.studio-input, .studio-select, .studio-textarea {
  background: #ffffff; border: 1px solid #000000; border-radius: 6px; color: #000000;
  padding: 9px 11px; font-size: 13.5px; font-family: var(--ir-font-body); outline: none;
  transition: border-color .15s ease, background .15s ease; width: 100%;
}
.studio-textarea { resize: vertical; min-height: 64px; line-height: 1.4; }
.studio-input:focus, .studio-select:focus, .studio-textarea:focus { border-color: var(--ir-cyan); background: #ffffff; }
.studio-input::placeholder, .studio-textarea::placeholder { color: #808080; }
.studio-row { display: flex; gap: 10px; }
.studio-row > * { flex: 1; min-width: 0; }

.studio-btn {
  appearance: none; border: 1px solid #000000; background: #ffffff; color: #1a1a1a;
  border-radius: 6px; padding: 8px 12px; font-size: 12.5px; font-weight: 600;
  font-family: var(--ir-font-body); cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  transition: background .15s ease, border-color .15s ease, color .15s ease, transform .1s ease;
  white-space: nowrap;
}
.studio-btn:hover { background: #000000; color: #ffffff; border-color: #000000; }
.studio-btn:active { transform: translateY(1px); }
.studio-btn.is-primary { background: var(--ir-cyan); border-color: var(--ir-cyan); color: #ffffff; }
.studio-btn.is-primary:hover { background: #0a575d; }
.studio-btn.is-active { background: rgba(12,109,117,.1); border-color: var(--ir-cyan); color: var(--ir-cyan); }
.studio-btn.is-icon { padding: 8px 10px; }
.studio-btn.is-block { width: 100%; }
.studio-btn:disabled { opacity: .4; cursor: not-allowed; }

.studio-chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
.studio-chip {
  font-family: var(--ir-font-data); font-size: 11px; letter-spacing: .04em;
  border: 1px solid #000000; background: #ffffff; color: #454545;
  border-radius: 5px; padding: 5px 8px 5px 10px;
  display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
  transition: border-color .15s ease, color .15s ease, background .15s ease;
}
.studio-chip.is-flagged { border-color: var(--ir-red); color: var(--ir-red); background: rgba(184,50,38,.07); }
.studio-chip-x { border: none; background: transparent; color: inherit; opacity: .6; display: flex; align-items: center; cursor: pointer; padding: 0; }
.studio-chip-x:hover { opacity: 1; }

.studio-hint { font-size: 11.5px; color: #595959; line-height: 1.5; }
.studio-slider-row { display: flex; align-items: center; gap: 10px; }
.studio-slider { flex: 1; accent-color: var(--ir-amber); }
.studio-slider-value { font-family: var(--ir-font-data); font-size: 13px; color: var(--ir-amber); width: 44px; text-align: right; flex: 0 0 auto; }
.studio-divider { height: 1px; background: #000000; margin: 2px 0; }
.studio-toggle { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: #1f1f1f; cursor: pointer; }
.studio-toggle input { accent-color: var(--ir-cyan); }

.studio-anim-box {
  border: 1px solid #000000; border-radius: 6px; padding: 10px 11px 11px;
  background: #ffffff; display: flex; flex-direction: column; gap: 8px;
}
.studio-anim-label {
  font-family: var(--ir-font-data); font-size: 10.5px; letter-spacing: .1em; text-transform: uppercase;
  color: #595959;
}

.studio-file-row { display: flex; align-items: center; gap: 8px; }
.studio-file-btn {
  position: relative; overflow: hidden;
}
.studio-file-btn input[type="file"] {
  position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%;
}
.studio-file-name {
  flex: 1; min-width: 0; font-size: 12px; color: #454545;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-family: var(--ir-font-data);
}
.studio-file-name.is-empty { color: #808080; }

  .studio-main { flex-direction: column; }
  .studio-sidebar { flex: 0 0 auto; max-width: none; border-left: none; border-top: 1px solid #000000; }
`;

/* ==========================================================================
   Flow config — this array IS the flowchart. Add/remove/reorder a step by
   editing this list; the strip and the transport controls follow it.
   ========================================================================== */

const STEPS = [
  { key: 'subject', label: 'Subject' },
  { key: 'evidence', label: 'Evidence' },
  { key: 'motive', label: 'Motive' },
  { key: 'alternatives', label: 'Alternatives' },
  { key: 'wtf', label: 'WTF Level' },
  { key: 'verdict', label: 'Verdict' },
];

function verdictClass(verdict = '') {
  const v = verdict.toUpperCase();
  if (v.includes('ACCEPTED') && !v.includes('NO DEFENSE')) return 'verdict-accepted';
  if (v.includes('QUESTIONABLE')) return 'verdict-questionable';
  return '';
}

/* ==========================================================================
   Streaming helpers — shared by the room (for its built-in defaults) and
   the Studio sidebar (so the "Auto" hint shows the same number).
   ========================================================================== */

function clampMs(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

// Short title — types fast, small floor/ceiling since it's rarely more than a few words.
function defaultSubjectMs(text) {
  return clampMs((text || '').length * 60, 500, 3200);
}

// Full sentence(s) — slower per-character so long motives don't fly by, longer ceiling.
function defaultMotiveMs(text) {
  return clampMs((text || '').length * 28, 900, 7000);
}

// A list — total time is driven by combined length, but never faster than
// ~380ms per item so a 2-word list doesn't blink in.
function defaultAlternativesMs(alts) {
  const list = alts || [];
  const chars = list.reduce((sum, a) => sum + a.length, 0);
  return clampMs(Math.max(chars * 28, list.length * 380), 500, 6000);
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = () => setReduced(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);
  return reduced;
}

// Reveals `text` character-by-character over `durationMs`, restarting every
// time `active` flips true (i.e. every time this step is (re-)entered).
function useTypewriter(text, durationMs, active) {
  const reducedMotion = usePrefersReducedMotion();
  const safeText = text || '';
  const [shown, setShown] = useState(safeText.length);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    if (reducedMotion || !safeText || durationMs <= 0) {
      setShown(safeText.length);
      return undefined;
    }
    setShown(0);
    const start = performance.now();
    function tick(now) {
      const progress = Math.min(1, (now - start) / durationMs);
      setShown(Math.round(progress * safeText.length));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeText, durationMs, active, reducedMotion]);

  return { visible: safeText.slice(0, shown), done: shown >= safeText.length };
}

/* ==========================================================================
   InvestigationRoom — pure presentation. Shows exactly one step at a time;
   `step` picks which one. Nothing here reads from a form directly, so it
   stays reusable outside the Studio shell later (e.g. a video pipeline).

   Subject / Motive / Alternatives can stream in over a given duration:
   pass *StreamMs to control it, or leave it unset and the room falls back
   to a sensible default based on how much text there is.

   Optional sound: pass typingSoundUrl to loop a sound while Subject/Motive
   are typing in, and stampSoundUrl to fire once when the Verdict lands.
   ========================================================================== */

function InvestigationRoom({
  caseNumber = 1,
  subject = 'Untitled Decision',
  category = 'LOGIC FAILURE',
  evidenceImage = '',
  evidenceAlt = 'Evidence under review',
  motive = '',
  alternatives = [],
  flaggedAlternative = '',
  previousWtf = 0,
  currentWtf = 0,
  verdict = 'NO DEFENSE ACCEPTED',
  step = 'subject',
  onStepClick,
  subjectStreamMs = null,
  motiveStreamMs = null,
  alternativesStreamMs = null,
  typingSoundUrl = '',
  stampSoundUrl = '',
  soundEnabled = true,
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setMounted(true), 20);
    return () => clearTimeout(id);
  }, []);

  const stepIndex = Math.max(0, STEPS.findIndex((s) => s.key === step));
  const caseNumberDisplay = String(caseNumber).padStart(3, '0');

  const resolvedSubjectMs = subjectStreamMs != null ? subjectStreamMs : defaultSubjectMs(subject);
  const resolvedMotiveMs = motiveStreamMs != null ? motiveStreamMs : defaultMotiveMs(motive);
  const resolvedAlternativesMs = alternativesStreamMs != null ? alternativesStreamMs : defaultAlternativesMs(alternatives);

  // Hooks run unconditionally every render (rules of hooks) — each one only
  // actually animates while its own step is the active one.
  const subjectStream = useTypewriter(subject, resolvedSubjectMs, step === 'subject');
  const motiveStream = useTypewriter(motive, resolvedMotiveMs, step === 'motive');

  const altStepMs = alternatives.length > 0 ? resolvedAlternativesMs / alternatives.length : 0;

  // WTF meter animates fresh every time the WTF step is (re-)entered.
  const [wtfDisplay, setWtfDisplay] = useState(previousWtf);
  useEffect(() => {
    if (step !== 'wtf') return undefined;
    setWtfDisplay(previousWtf);
    const id = setTimeout(() => setWtfDisplay(currentWtf), 200);
    return () => clearTimeout(id);
  }, [step, previousWtf, currentWtf]);

  const meterPct = useMemo(() => `${(Math.max(0, Math.min(10, wtfDisplay)) / 10) * 100}%`, [wtfDisplay]);

  /* ---- Sound: a looping typing sound while Subject/Motive are streaming,
     a one-shot stamp sound the instant the Verdict step is entered. ---- */
  const typingAudioRef = useRef(null);
  const stampAudioRef = useRef(null);

  const isTypingText =
    soundEnabled &&
    !!typingSoundUrl &&
    ((step === 'subject' && !!subject && !subjectStream.done) ||
      (step === 'motive' && !!motive && !motiveStream.done));

  useEffect(() => {
    const audio = typingAudioRef.current;
    if (!audio) return;
    if (isTypingText) {
      audio.currentTime = 0;
      const p = audio.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      audio.pause();
    }
  }, [isTypingText]);

  useEffect(() => {
    if (!soundEnabled || !stampSoundUrl) return undefined;
    if (step !== 'verdict') return undefined;
    const audio = stampAudioRef.current;
    if (!audio) return undefined;
    audio.currentTime = 0;
    const p = audio.play();
    if (p && p.catch) p.catch(() => {});
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, stampSoundUrl, soundEnabled]);

  function renderFocus() {
    switch (step) {
      case 'subject':
        return (
          <div className="ir-focus-inner" key="subject">
            <div className="ir-focus-eyebrow">Subject Under Review</div>
            <div className="ir-focus-title">
              {subjectStream.visible}
              {!subjectStream.done && <span className="ir-type-cursor" />}
            </div>
          </div>
        );
      case 'evidence':
        return (
          <div className="ir-focus-inner" key="evidence">
            <div className="ir-focus-eyebrow">Evidence Feed</div>
            <div className="ir-evidence-frame">
              <div className="ir-evidence-corner tl" />
              <div className="ir-evidence-corner tr" />
              <div className="ir-evidence-corner bl" />
              <div className="ir-evidence-corner br" />
              {evidenceImage ? (
                <img className="ir-evidence-img" src={evidenceImage} alt={evidenceAlt} />
              ) : (
                <div className="ir-evidence-placeholder">Drop movie frame / screenshot / freeze-frame here</div>
              )}
              <div className="ir-scanline" />
            </div>
          </div>
        );
      case 'motive':
        return (
          <div className="ir-focus-inner" key="motive">
            <div className="ir-focus-eyebrow">Motive</div>
            <div className="ir-focus-text">
              {motive ? motiveStream.visible : 'No motive on record.'}
              {motive && !motiveStream.done && <span className="ir-type-cursor" />}
            </div>
          </div>
        );
      case 'alternatives':
        return (
          <div className="ir-focus-inner" key="alternatives">
            <div className="ir-focus-eyebrow">Available Alternatives</div>
            <ul className="ir-alt-grid">
              {alternatives.length === 0 && (
                <li style={{ opacity: 0.5, animationDelay: '0s' }}>No alternatives on record</li>
              )}
              {alternatives.map((alt, i) => (
                <li
                  key={alt}
                  className={alt === flaggedAlternative ? 'is-flagged' : ''}
                  style={{ animationDelay: `${(i * altStepMs) / 1000}s` }}
                >
                  {alt}
                </li>
              ))}
            </ul>
          </div>
        );
      case 'wtf':
        return (
          <div className="ir-focus-inner" key="wtf">
            <div className="ir-focus-eyebrow">WTF Level</div>
            <div className="ir-meter-big">
              <div className="ir-meter-track">
                <div className="ir-meter-fill" style={{ width: meterPct }} />
              </div>
              <div className="ir-meter-ticks">{Array.from({ length: 11 }).map((_, i) => <span key={i} />)}</div>
            </div>
            <div className="ir-meter-value">{wtfDisplay.toFixed(1)}</div>
            <div className="ir-meter-max">out of 10.0</div>
          </div>
        );
      case 'verdict':
        return (
          <div className="ir-focus-inner" key="verdict">
            <div className="ir-focus-eyebrow">Verdict</div>
            <div className={`ir-stamp ${verdictClass(verdict)}`}>{verdict}</div>
          </div>
        );
      default:
        return null;
    }
  }

  return (
    <div className="ir-viewport">
      <div className={`ir-stage${mounted ? ' is-mounted' : ''}`}>
        <div className="ir-walls" />

        {typingSoundUrl && <audio ref={typingAudioRef} src={typingSoundUrl} loop preload="auto" />}
        {stampSoundUrl && <audio ref={stampAudioRef} src={stampSoundUrl} preload="auto" />}

        <div className="ir-frame">
          <div className="ir-header">
            <span className="ir-kicker">Internal Review &middot; Do Not Distribute</span>
            <div className="ir-header-right">
              <span className="ir-category">{category}</span>
              <span className="ir-case-tag">CASE #{caseNumberDisplay}</span>
            </div>
          </div>

          <div className="ir-flow">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.key}>
                <button
                  type="button"
                  className={`ir-node-btn${i < stepIndex ? ' is-done' : ''}${i === stepIndex ? ' is-current' : ''}`}
                  onClick={() => onStepClick && onStepClick(s.key)}
                >
                  <span className="ir-node-dot" />
                  <span className="ir-node-label">{s.label}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <span className={`ir-connector${i < stepIndex ? ' is-done' : ''}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="ir-focus">{renderFocus()}</div>
        </div>

        <div className="ir-vignette" />
      </div>
    </div>
  );
}

/* ==========================================================================
   Studio — the editor shell: fill in the fields once, then step through
   the flow (Prev/Next, click a node to jump, or hit Play to autoplay).
   ========================================================================== */

const CATEGORIES = ['LOGIC FAILURE', 'WRITER CONVENIENCE', 'CHARACTER MALFUNCTION', 'CINEMATIC NONSENSE', 'REALITY BREACH'];
const VERDICT_PRESETS = ['NO DEFENSE ACCEPTED', 'ACCEPTED', 'QUESTIONABLE', 'REJECTED', 'GUILTY', 'ABSOLUTELY NOT'];

const STARTER = {
  caseNumber: 27,
  subject: 'Entering the Basement',
  category: 'LOGIC FAILURE',
  evidenceImage: '',
  motive: 'Character heard a noise and, despite having a phone, three able-bodied friends, and a working front door, decided the noise was best investigated alone and unarmed.',
  alternatives: ['LEAVE HOUSE', 'CALL POLICE', 'STAY UPSTAIRS', 'ENTER BASEMENT'],
  flaggedAlternative: 'ENTER BASEMENT',
  previousWtf: 0,
  currentWtf: 8.5,
  verdict: 'NO DEFENSE ACCEPTED',
  // Streaming config per section. `seconds: null` while auto=true just
  // means "follow the length-based default" — see defaultXMs() above.
  subjectStream: { auto: true, seconds: null },
  motiveStream: { auto: true, seconds: null },
  alternativesStream: { auto: true, seconds: null },
};

// Small reusable control: Auto (length-based) vs a manual seconds slider.
function StreamControl({ cfg, defaultMs, min = 0, max = 10, onChange }) {
  const defaultSec = defaultMs / 1000;
  const currentSec = cfg.seconds != null ? cfg.seconds : defaultSec;
  return (
    <div className="studio-anim-box">
      <label className="studio-toggle">
        <input
          type="checkbox"
          checked={cfg.auto}
          onChange={(e) => onChange({ ...cfg, auto: e.target.checked })}
        />
        Auto — {defaultSec.toFixed(1)}s (based on text length)
      </label>
      {!cfg.auto && (
        <div className="studio-slider-row">
          <input
            className="studio-slider"
            type="range"
            min={min}
            max={max}
            step={0.1}
            value={currentSec}
            onChange={(e) => onChange({ ...cfg, seconds: Number(e.target.value) })}
          />
          <span className="studio-slider-value">{currentSec.toFixed(1)}s</span>
        </div>
      )}
    </div>
  );
}

// File picker for an mp3 — stores an in-memory object URL (session-only,
// nothing is uploaded anywhere) plus the original filename for display.
function SoundFilePicker({ label, sound, onChange, onClear, onPreview }) {
  function handleFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (sound.url) URL.revokeObjectURL(sound.url);
    const url = URL.createObjectURL(file);
    onChange({ url, name: file.name });
    e.target.value = '';
  }

  return (
    <div className="studio-field">
      <label className="studio-label">{label}</label>
      <div className="studio-file-row">
        <button type="button" className="studio-btn is-icon studio-file-btn">
          <Upload size={13} />
          <input type="file" accept="audio/mpeg,audio/mp3,.mp3,audio/*" onChange={handleFile} />
        </button>
        <span className={`studio-file-name${sound.url ? '' : ' is-empty'}`}>
          {sound.name || 'No file chosen'}
        </span>
        {sound.url && (
          <>
            <button type="button" className="studio-btn is-icon" onClick={onPreview} title="Preview">
              <Play size={13} />
            </button>
            <button type="button" className="studio-btn is-icon" onClick={onClear} title="Remove">
              <X size={13} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function InvestigationRoomStudio() {
  const [form, setForm] = useState(STARTER);
  const [newAlt, setNewAlt] = useState('');
  const [stepIndex, setStepIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(false);

  const [typingSound, setTypingSound] = useState({ url: '', name: '' });
  const [stampSound, setStampSound] = useState({ url: '', name: '' });
  const [soundEnabled, setSoundEnabled] = useState(true);

  const step = STEPS[stepIndex].key;

  useEffect(() => {
    if (!autoplay) return undefined;
    const id = setInterval(() => {
      setStepIndex((i) => (i + 1) % STEPS.length);
    }, 2200);
    return () => clearInterval(id);
  }, [autoplay]);

  // Revoke blob URLs on unmount so the browser can free the memory.
  useEffect(() => {
    return () => {
      if (typingSound.url) URL.revokeObjectURL(typingSound.url);
      if (stampSound.url) URL.revokeObjectURL(stampSound.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function addAlternative() {
    const val = newAlt.trim().toUpperCase();
    if (!val || form.alternatives.includes(val)) return;
    set('alternatives', [...form.alternatives, val]);
    setNewAlt('');
  }

  function removeAlternative(alt) {
    set('alternatives', form.alternatives.filter((x) => x !== alt));
    if (form.flaggedAlternative === alt) set('flaggedAlternative', '');
  }

  function applyWtf(value) {
    set('previousWtf', form.currentWtf);
    set('currentWtf', value);
  }

  function goTo(key) {
    setAutoplay(false);
    const i = STEPS.findIndex((s) => s.key === key);
    if (i >= 0) setStepIndex(i);
  }

  function resetAll() {
    setForm(STARTER);
    setStepIndex(0);
    setAutoplay(false);
  }

  function previewSound(url) {
    if (!url) return;
    const audio = new Audio(url);
    audio.play().catch(() => {});
  }

  function clearTypingSound() {
    if (typingSound.url) URL.revokeObjectURL(typingSound.url);
    setTypingSound({ url: '', name: '' });
  }

  function clearStampSound() {
    if (stampSound.url) URL.revokeObjectURL(stampSound.url);
    setStampSound({ url: '', name: '' });
  }

  // Resolve the actual millisecond durations passed to the room: either the
  // length-based default, or the person's manual override.
  const subjectDefaultMs = defaultSubjectMs(form.subject);
  const motiveDefaultMs = defaultMotiveMs(form.motive);
  const alternativesDefaultMs = defaultAlternativesMs(form.alternatives);

  const subjectStreamMs = form.subjectStream.auto
    ? subjectDefaultMs
    : Math.max(0, (form.subjectStream.seconds ?? subjectDefaultMs / 1000) * 1000);
  const motiveStreamMs = form.motiveStream.auto
    ? motiveDefaultMs
    : Math.max(0, (form.motiveStream.seconds ?? motiveDefaultMs / 1000) * 1000);
  const alternativesStreamMs = form.alternativesStream.auto
    ? alternativesDefaultMs
    : Math.max(0, (form.alternativesStream.seconds ?? alternativesDefaultMs / 1000) * 1000);

  return (
    <div className="studio">
      <style>{ROOM_STYLES}</style>
      <div className="studio-topbar">
        <div className="studio-brand">
          <span className="studio-brand-dot" />
          <div>
            <div className="studio-brand-name">Investigation Room</div>
            <div className="studio-brand-sub">Case Editor</div>
          </div>
        </div>
        <div className="studio-topbar-actions">
          <button
            className={`studio-btn is-icon${soundEnabled ? ' is-active' : ''}`}
            onClick={() => setSoundEnabled((s) => !s)}
            title={soundEnabled ? 'Mute sound' : 'Unmute sound'}
          >
            {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
          </button>
          <button className="studio-btn" onClick={resetAll}><RotateCcw size={14} /> Reset to example</button>
        </div>
      </div>

      <div className="studio-main">
        <div className="studio-stage-wrap">
          <div className="studio-stage-inner">
            <InvestigationRoom
              caseNumber={form.caseNumber}
              subject={form.subject}
              category={form.category}
              evidenceImage={form.evidenceImage}
              motive={form.motive}
              alternatives={form.alternatives}
              flaggedAlternative={form.flaggedAlternative}
              previousWtf={form.previousWtf}
              currentWtf={form.currentWtf}
              verdict={form.verdict}
              step={step}
              onStepClick={goTo}
              subjectStreamMs={subjectStreamMs}
              motiveStreamMs={motiveStreamMs}
              alternativesStreamMs={alternativesStreamMs}
              typingSoundUrl={typingSound.url}
              stampSoundUrl={stampSound.url}
              soundEnabled={soundEnabled}
            />

            <div className="studio-transport">
              <button
                className="studio-btn is-icon"
                onClick={() => { setAutoplay(false); setStepIndex((i) => Math.max(0, i - 1)); }}
                disabled={stepIndex === 0}
              >
                <ChevronLeft size={16} />
              </button>
              <button className="studio-btn is-icon" onClick={() => setAutoplay((a) => !a)}>
                {autoplay ? <Pause size={16} /> : <Play size={16} />}
              </button>
              <span className="studio-transport-count">
                Step {stepIndex + 1} of {STEPS.length} &middot; {STEPS[stepIndex].label}
              </span>
              <button
                className="studio-btn is-icon"
                onClick={() => { setAutoplay(false); setStepIndex((i) => Math.min(STEPS.length - 1, i + 1)); }}
                disabled={stepIndex === STEPS.length - 1}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>

        <div className="studio-sidebar">
          <div className="studio-section">
            <div className="studio-section-title">Case Details</div>
            <div className="studio-row">
              <div className="studio-field">
                <label className="studio-label">Case #</label>
                <input
                  className="studio-input"
                  type="number"
                  min={1}
                  value={form.caseNumber}
                  onChange={(e) => set('caseNumber', Number(e.target.value) || 1)}
                />
              </div>
              <div className="studio-field" style={{ flex: 2 }}>
                <label className="studio-label">Category</label>
                <select className="studio-select" value={form.category} onChange={(e) => set('category', e.target.value)}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="studio-field">
              <label className="studio-label">Subject (the decision on trial)</label>
              <input
                className="studio-input"
                type="text"
                placeholder="e.g. Entering the Basement"
                value={form.subject}
                onChange={(e) => set('subject', e.target.value)}
              />
            </div>
            <div className="studio-field">
              <label className="studio-label">Stream-in time</label>
              <StreamControl
                cfg={form.subjectStream}
                defaultMs={subjectDefaultMs}
                max={6}
                onChange={(cfg) => set('subjectStream', cfg)}
              />
            </div>
          </div>

          <div className="studio-divider" />

          <div className="studio-section">
            <div className="studio-section-title">Evidence</div>
            <div className="studio-field">
              <label className="studio-label">Image URL</label>
              <input
                className="studio-input"
                type="text"
                placeholder="https://..."
                value={form.evidenceImage}
                onChange={(e) => set('evidenceImage', e.target.value)}
              />
            </div>
          </div>

          <div className="studio-divider" />

          <div className="studio-section">
            <div className="studio-section-title">Motive</div>
            <div className="studio-field">
              <textarea
                className="studio-textarea"
                placeholder="Why the character supposedly did this..."
                value={form.motive}
                onChange={(e) => set('motive', e.target.value)}
              />
            </div>
            <div className="studio-field">
              <label className="studio-label">Stream-in time</label>
              <StreamControl
                cfg={form.motiveStream}
                defaultMs={motiveDefaultMs}
                max={10}
                onChange={(cfg) => set('motiveStream', cfg)}
              />
            </div>
          </div>

          <div className="studio-divider" />

          <div className="studio-section">
            <div className="studio-section-title">Alternatives</div>
            <div className="studio-row">
              <input
                className="studio-input"
                type="text"
                placeholder="Add an option, e.g. Call Police"
                value={newAlt}
                onChange={(e) => setNewAlt(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addAlternative()}
              />
              <button className="studio-btn is-icon" onClick={addAlternative}><Plus size={14} /></button>
            </div>
            <div className="studio-chip-row">
              {form.alternatives.map((alt) => (
                <span
                  key={alt}
                  className={`studio-chip${alt === form.flaggedAlternative ? ' is-flagged' : ''}`}
                  onClick={() => set('flaggedAlternative', alt === form.flaggedAlternative ? '' : alt)}
                  title="Click to mark as the flagged decision"
                >
                  {alt}
                  <button
                    className="studio-chip-x"
                    onClick={(e) => { e.stopPropagation(); removeAlternative(alt); }}
                    aria-label={`Remove ${alt}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="studio-hint">Click a chip to flag it red as the decision under fire.</div>
            <div className="studio-field">
              <label className="studio-label">Stream-in time (total, spread across all items)</label>
              <StreamControl
                cfg={form.alternativesStream}
                defaultMs={alternativesDefaultMs}
                max={8}
                onChange={(cfg) => set('alternativesStream', cfg)}
              />
            </div>
          </div>

          <div className="studio-divider" />

          <div className="studio-section">
            <div className="studio-section-title">WTF Level</div>
            <div className="studio-slider-row">
              <input
                className="studio-slider"
                type="range"
                min={0}
                max={10}
                step={0.5}
                value={form.currentWtf}
                onChange={(e) => applyWtf(Number(e.target.value))}
              />
              <span className="studio-slider-value">{form.currentWtf.toFixed(1)}</span>
            </div>
            <div className="studio-hint">Animates from {form.previousWtf.toFixed(1)} to {form.currentWtf.toFixed(1)} on the WTF step.</div>
          </div>

          <div className="studio-divider" />

          <div className="studio-section">
            <div className="studio-section-title">Verdict</div>
            <div className="studio-chip-row">
              {VERDICT_PRESETS.map((v) => (
                <button
                  key={v}
                  className={`studio-btn${form.verdict === v ? ' is-active' : ''}`}
                  onClick={() => set('verdict', v)}
                >
                  {v}
                </button>
              ))}
            </div>
            <input
              className="studio-input"
              type="text"
              placeholder="Custom verdict text"
              value={form.verdict}
              onChange={(e) => set('verdict', e.target.value)}
            />
          </div>

          <div className="studio-divider" />

          <div className="studio-section">
            <div className="studio-section-title">Sound</div>
            <SoundFilePicker
              label="Typing sound (loops while Subject/Motive stream in)"
              sound={typingSound}
              onChange={setTypingSound}
              onClear={clearTypingSound}
              onPreview={() => previewSound(typingSound.url)}
            />
            <SoundFilePicker
              label="Stamp sound (plays once the Verdict lands)"
              sound={stampSound}
              onChange={setStampSound}
              onClear={clearStampSound}
              onPreview={() => previewSound(stampSound.url)}
            />
            <div className="studio-hint">
              Files stay in this browser tab only — nothing is uploaded. The speaker icon top-right mutes both.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}